import React, { useState } from 'react';
import { Film, MapPin, ShieldCheck, CheckCircle2, ArrowRight } from 'lucide-react';

interface TimelineStage {
  id: string;
  step: string;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  deliverables: string[];
  specs: Record<string, string>;
}

const STAGES: TimelineStage[] = [
  {
    id: 'pre',
    step: '01',
    title: 'PRE-PRODUCTION',
    subtitle: 'Script coverage, financing, 3D pre-vis & lens choices',
    description: 'Comprehensive technical and artistic preparation including screenplay analysis, co-production packaging, talent attachments, 3D pre-visualization in Unreal Engine, location scouting, and optical lens tuning.',
    image: 'https://images.unsplash.com/photo-1478720568477-152d9b164e26?auto=format&fit=crop&w=1000&q=80',
    deliverables: ['Original Screenplays', 'Co-Production Packaging', 'Unreal Engine Pre-Vis', 'Optical Lens Tests'],
    specs: { 'Development Cycle': '2-4 Months', 'Pre-Vis Engine': 'Unreal Engine 5.4', 'Packaging': 'Domestic & Global Co-Pro' }
  },
  {
    id: 'prod',
    step: '02',
    title: 'PRODUCTION',
    subtitle: 'ARRI 35, Sony Venice 2, soundstages & multi-camera setups',
    description: 'Execution of principal photography powered by elite DOP camera crews, dual 8K large format camera packages, specialized motion control, high-speed pursuit vehicles, and ISO-certified unit hospitality.',
    image: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&w=1000&q=80',
    deliverables: ['8K RAW Camera Masters', 'Elite DOP Camera Units', 'On-Set DIT Dailies', 'Soundstage & Motion Control'],
    specs: { 'Resolution': '4.6K - 8K RAW', 'Color Science': 'ARRI REVEAL / S-Gamut3', 'Gear Fleet': 'Full Stargaze Inventory' }
  },
  {
    id: 'post',
    step: '03',
    title: 'POST-PRODUCTION',
    subtitle: 'Photorealistic VFX, 8K grading, Foley & Dolby Atmos mastering',
    description: 'Full-spectrum visual and acoustic post-production in partnership with Dawn Studios, featuring photorealistic 3D CGI compositing, uncompressed 8K color grading, Dolby Atmos 7.1.4 immersive spatial sound mixing, and DCI-compliant DCP mastering.',
    image: 'https://images.unsplash.com/photo-1578632767115-351597cf2477?auto=format&fit=crop&w=1000&q=80',
    deliverables: ['VFX Compositing (Dawn Studios)', '8K Color LUT Grading', 'Dolby Atmos 7.1.4 Spatial Mix', 'DCI DCP Theatrical Masters'],
    specs: { 'VFX Partner': 'Dawn Studio & Anikesh Trivedi', 'Audio Mastering': 'Dolby Atmos 7.1.4', 'Color Pipeline': 'ACES / 8K Rec.2020' }
  },
  {
    id: 'pr',
    step: '04',
    title: 'PR & MARKETING',
    subtitle: 'Digital promotions, trailer launches, talent tours & celebrity PR',
    description: 'Strategic film promotion and multimedia entertainment PR in collaboration with Khushi Advertising and Hungama Media. Encompasses theatrical trailer rollouts, outdoor hoardings, press conferences, red-carpet junkets, and global film festival PR campaigns.',
    image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1000&q=80',
    deliverables: ['Theatrical Trailers & Cut-downs', 'Press Junkets & Talent Tours', 'Digital Omnichannel Campaigns', 'Festival Awards PR Strategy'],
    specs: { 'Campaign Reach': 'National & Global B2B', 'Media Alliances': 'Khushi & Hungama Network', 'Digital PR': 'OTT & Broadcast' }
  },
  {
    id: 'dist',
    step: '05',
    title: 'RELEASE & DISTRIBUTION',
    subtitle: 'Worldwide theatrical, digital OTT streaming & linear broadcast rights',
    description: 'Connecting regional and national cinema with international buyers, OTT platforms, theatrical circuits, and linear broadcasters. Handled across 120+ territories in partnership with Jasinto Worldwide Distribution and UFO / Janta Cinema.',
    image: 'https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1000&q=80',
    deliverables: ['Worldwide Theatrical Syndication', 'OTT Streaming Licenses', 'Inflight & Television Rights', 'Format & Remake Licensing'],
    specs: { 'Territory Reach': '120+ Global Territories', 'Distribution Partner': 'Jasinto & UFO Networks', 'Formats': 'IMAX, DCI DCP & OTT 4K' }
  }
];

export const ProductionTimeline: React.FC = () => {
  const [activeStage, setActiveStage] = useState<TimelineStage>(STAGES[0]); // Default to Pre-Production

  return (
    <section className="py-24 bg-[#F8F9FA] border-t border-zinc-200/80 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 border-b border-zinc-200 pb-6 gap-4">
          <div>
            <span className="text-[#B45309] text-xs font-mono font-bold tracking-widest uppercase block mb-2">
              ECOSYSTEM // PRODUCTION PIPELINE
            </span>
            <h2 className="text-3xl sm:text-5xl font-black font-serif-cinematic text-zinc-900 uppercase">
              THE PRODUCTION TIMELINE
            </h2>
          </div>
          <p className="text-zinc-600 font-sans text-xs sm:text-sm max-w-md">
            From initial screenplay coverage to large-format principal photography across global soundstages.
          </p>
        </div>

        {/* Timeline Stage Selector Buttons */}
        <div className="flex flex-wrap gap-2 mb-12 border-b border-zinc-200 pb-4 overflow-x-auto">
          {STAGES.map((s) => {
            const isSelected = activeStage.id === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setActiveStage(s)}
                data-cursor="VIEW"
                className={`px-5 py-3 rounded-xl font-mono text-xs font-bold uppercase tracking-wider transition flex items-center gap-3 ${
                  isSelected
                    ? 'bg-[#D97706] text-white shadow-lg shadow-amber-500/20'
                    : 'bg-white text-zinc-600 border border-zinc-200 hover:text-zinc-900 hover:border-amber-400'
                }`}
              >
                <span className={isSelected ? 'text-white font-extrabold' : 'text-[#B45309]'}>
                  {s.step}
                </span>
                <span>{s.title.split('&')[0]}</span>
              </button>
            );
          })}
        </div>

        {/* Selected Stage Detail Showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center bg-white border border-zinc-200 rounded-3xl p-6 sm:p-10 relative overflow-hidden shadow-lg">
          {/* Left Text Detail */}
          <div className="lg:col-span-6 space-y-6">
            <div className="flex items-center gap-3 text-xs font-mono text-[#B45309]">
              <span className="px-2.5 py-1 rounded bg-amber-500/10 border border-amber-500/30 font-bold">
                STAGE {activeStage.step}
              </span>
              <span className="uppercase tracking-widest font-semibold">{activeStage.subtitle}</span>
            </div>

            <h3 className="text-3xl sm:text-4xl font-bold font-serif-cinematic text-zinc-900 leading-tight">
              {activeStage.title}
            </h3>

            <p className="text-zinc-600 font-sans text-sm sm:text-base leading-relaxed">
              {activeStage.description}
            </p>

            {/* Deliverables Checklist */}
            <div className="space-y-2 pt-2">
              <span className="text-xs font-mono text-zinc-500 font-bold uppercase tracking-widest block mb-2">
                KEY DELIVERABLES:
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {activeStage.deliverables.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs font-sans text-zinc-800 font-medium">
                    <CheckCircle2 className="w-4 h-4 text-[#D97706] shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Technical Specs Bar */}
            <div className="pt-4 border-t border-zinc-100 grid grid-cols-3 gap-4 font-mono text-xs">
              {Object.entries(activeStage.specs).map(([key, val]) => (
                <div key={key}>
                  <span className="text-zinc-400 block text-[10px] uppercase font-bold">{key}</span>
                  <span className="text-[#B45309] font-bold">{val}</span>
                </div>
              ))}
            </div>

            <div className="pt-2">
              <a
                href="/enquiry"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-amber-50 hover:bg-[#D97706] hover:text-white border border-amber-500/40 text-[#B45309] text-xs font-mono font-bold tracking-wider uppercase transition"
              >
                REQUEST STAGE SUPPORT <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Right Stage Imagery */}
          <div className="lg:col-span-6">
            <div className="aspect-[16/10] rounded-2xl overflow-hidden border border-zinc-200 relative group shadow-md">
              <img
                src={activeStage.image}
                alt={activeStage.title}
                className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
